import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  type Paper,
  Typography,
} from '@mui/material';
import { TextField, type TextFieldProps, UserAvatar } from '@side/pantry';
import {
  type ComponentProps,
  type HTMLAttributes,
  type JSXElementConstructor,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebounce } from 'react-use';
import { useInteractionParams } from '@/common/hooks/useInteractionParams';
import { useUserIntegrations } from '@/common/hooks/useUserIntegrations';
import { InteractionType } from '@/common/types/interaction';
import { formatTeamMemberIds } from '@/common/utils/formatTeamMemberId';
import { highlightSubstring } from '@/common/utils/string';
import {
  ContactSuggestionsNoResults,
  EMPTY_PLACEHOLDER_ID,
  EMPTY_PLACEHOLDER_OPTION,
} from '@/components/ContactSuggestionsNoResults';
import {
  type ContactSuggestion,
  mapContactSuggestionToTeamMembershipIdentifiers,
  useContactSuggestions,
} from '$contact-suggestions';
import {
  isOptionEqualToValue,
  mergeSelectedOption,
} from './ContactsAutocomplete.helpers';

export interface Props<
  PaperComponent extends JSXElementConstructor<
    HTMLAttributes<HTMLElement>
  > = typeof Paper,
> extends TextFieldProps {
  /**
   * ID of user to show contact suggestions for.
   */
  userId: string;
  /**
   * ID of team to show contact suggestions for. Required in order to show team
   * contact suggestions.
   */
  teamId?: string;
  /**
   * Callback fired when a suggested option is selected.
   */
  onSelectOption?: (value: ContactSuggestion) => void;
  /**
   * Whether to include google contact suggestions in results.
   * @default true
   */
  includeGoogleContacts?: boolean;
  /**
   * Whether to include follow-up boss contact suggestions in results.
   * @default true
   */
  includeFollowUpBossContacts?: boolean;
  /**
   * Whether to include side contact suggestions in results.
   * @default true
   */
  includeSideContacts?: boolean;
  /**
   * Whether to include team contact suggestions in results.
   * @default true
   */
  includeTeamContacts?: boolean;
  /**
   * Whether to include referral agent contact suggestions in results.
   * @default false
   */
  includeReferralAgents?: boolean;
  /**
   * Whether to exclude agents with non-compliant teams in the results.
   * @default false
   */
  excludeNonCompliantTeams?: boolean;
  /**
   * Array of agent IDs (formatted as "userId:teamId") to exclude from the dropdown options.
   * Useful for preventing duplicate agent selection across multiple autocomplete fields.
   */
  excludeAgentIds?: string[];
  /**
   * Filter agent suggestions by license states.
   * @default []
   */
  licenseStates?: string[];
  /**
   * Input value.
   */
  inputValue?: string;
  /**
   * Default value for input.
   */
  defaultValue?: ContactSuggestion;
  /**
   * Callback fired when input is cleared.
   */
  onClear?: () => void;
  /**
   * The component used to render the body of the popup.
   * @default Paper
   */
  PaperComponent?: PaperComponent;
  /**
   * Prop overrides for paper component.
   */
  paperProps?: ComponentProps<PaperComponent>;
  /**
   * Whether to enable free solo mode for MUI autocomplete.
   * @default true
   */
  freeSolo?: boolean;
  /**
   * Whether to show the clear icon in the text field.
   * @default false
   */
  hideClearIcon?: boolean;
}

/**
 * Displays team, side, and/or Google contact suggestions matching a search term.
 *
 * @example
 * ```tsx
 * import { Controller, useForm } from 'react-hook-form';
 * import { ContactsAutocomplete } from '@/components/ContactsAutocomplete';
 * import type { ContactSuggestion } from '$contact-suggestions';
 *
 * export function MyAutocompleteField() {
 *   const { control, setValue } = useForm();
 *
 *   const handleClear = () => {
 *     setValue('myField', '');
 *   };
 *
 *   const handleSelect = (contact: ContactSuggestion) {
 *     // Do something with contact
 *   };
 *
 *   return (
 *     <Controller
 *       control={control}
 *       name="myField"
 *       render={({ field: { onChange, onBlur, value } }) => (
 *         <ContactsAutocomplete
 *           userId="userId"
 *           teamId="teamId"
 *           label="First Name"
 *           inputValue={value}
 *           onChange={onChange}
 *           onBlur={onBlur}
 *           onClear={handleClear}
 *           onSelectOption={handleSelect}
 *         />
 *      )}
 *   );
 * }
 * ```
 */
export function ContactsAutocomplete({
  userId,
  teamId,
  onSelectOption,
  includeGoogleContacts = true,
  includeFollowUpBossContacts = true,
  includeSideContacts = true,
  includeTeamContacts = true,
  includeReferralAgents = false,
  excludeNonCompliantTeams = false,
  excludeAgentIds = [],
  licenseStates = [],
  hideClearIcon = false,
  inputValue: inputValueProp,
  defaultValue,
  onClear,
  PaperComponent,
  paperProps,
  freeSolo = true,
  inputRef,
  ...textFieldProps
}: Props) {
  const [selectedOption, setSelectedOption] = useState<
    ContactSuggestion | string | null
  >(defaultValue ?? null);
  const defaultValueInitialized = useRef(false);

  useEffect(() => {
    if (defaultValue && !defaultValueInitialized.current) {
      setSelectedOption(defaultValue);
      defaultValueInitialized.current = true;
    }
  }, [defaultValue]);

  const [internalInputValue, setInternalInputValue] = useState('');
  const inputValue = inputValueProp ?? internalInputValue;
  const [searchValue, setSearchValue] = useState(inputValue);
  useDebounce(() => setSearchValue(inputValue), 300, [inputValue]);

  const { interactionId, interactionType } = useInteractionParams();

  const { isIntegrationValid, loading: isLoadingIntegrations } =
    useUserIntegrations(userId);
  const isGoogleAccountLinked = isIntegrationValid('googlePeople');
  const isFollowUpBossAccountLinked = isIntegrationValid('followUpBoss');

  const { suggestions, loading: suggestionsLoading } = useContactSuggestions({
    userId,
    teamId,
    searchValue,
    includeGoogleContacts,
    includeFollowUpBossContacts,
    includeSideContacts,
    includeTeamContacts,
    includeReferralAgents,
    excludeNonCompliantTeams,
    licenseStates,
  });

  // Reset selection if value is emptied
  useEffect(() => {
    const isControlled = typeof inputValueProp === 'string';
    if (isControlled && !inputValueProp) {
      setSelectedOption(null);
    }
  }, [inputValueProp]);

  const loading = isLoadingIntegrations || suggestionsLoading;

  const options = useMemo(() => {
    // Filter out excluded agents
    const filteredSuggestions = suggestions.filter(
      (option) =>
        !formatTeamMemberIds(
          mapContactSuggestionToTeamMembershipIdentifiers(option),
        ).some((teamMemberId) => excludeAgentIds.includes(teamMemberId)),
    );

    if (!selectedOption || typeof selectedOption === 'string') {
      return filteredSuggestions;
    }

    // Ensure selected option is included in the options list
    const isSelectedInOptions = filteredSuggestions.some((option) =>
      isOptionEqualToValue(option, selectedOption),
    );

    return isSelectedInOptions
      ? filteredSuggestions
      : mergeSelectedOption(filteredSuggestions, selectedOption);
  }, [suggestions, excludeAgentIds, selectedOption]);

  return (
    <Autocomplete
      getOptionLabel={(option) => {
        if (typeof option !== 'object') return option;
        return option.displayName;
      }}
      options={options}
      noOptionsText={
        loading || !inputValue || freeSolo ? (
          ''
        ) : (
          <ContactSuggestionsNoResults
            interactionId={interactionId}
            interactionOwnerId={userId}
            googleAccountLinked={isGoogleAccountLinked}
            followUpBossAccountLinked={isFollowUpBossAccountLinked}
            thirdPartyContactsIncluded={
              includeGoogleContacts || includeFollowUpBossContacts
            }
            sx={{ pb: 0, pl: 0, pr: 0 }}
          />
        )
      }
      autoComplete
      freeSolo={freeSolo}
      forcePopupIcon={false}
      fullWidth
      includeInputInList
      filterOptions={(options: ContactSuggestion[]) => {
        if (
          freeSolo &&
          !options.length &&
          !loading &&
          searchValue.trim().length > 0
        ) {
          return [EMPTY_PLACEHOLDER_OPTION];
        }

        return options.filter((option) => {
          return option.displayName
            .toLowerCase()
            .includes(inputValue.toLowerCase());
        });
      }}
      value={selectedOption}
      inputValue={inputValue}
      groupBy={(option) => option.group}
      isOptionEqualToValue={isOptionEqualToValue}
      clearIcon={hideClearIcon ? null : undefined}
      onChange={(_, newValue) => {
        if (newValue && typeof newValue === 'object') {
          onSelectOption?.(newValue);
        }
        setSelectedOption(newValue);
      }}
      onInputChange={(_, newInputValue, reason) => {
        // `reason === 'reset'` fires when MUI internally syncs its input to a
        // changed controlled prop or selected value; it is not a user clear,
        // so don't propagate it as one.
        if (
          reason !== 'reset' &&
          (reason === 'clear' || newInputValue === '')
        ) {
          onClear?.();
        }
        setInternalInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          fixedHeight
          {...params}
          {...textFieldProps}
          ref={inputRef}
          slotProps={{
            ...params.slotProps,
            ...textFieldProps?.slotProps,
            input: {
              ...params.slotProps.input,
              ...textFieldProps?.slotProps?.input,
              endAdornment: loading ? (
                <CircularProgress sx={{ color: 'inherit' }} size={20} />
              ) : (
                params.slotProps.input.endAdornment
              ),
            },
            htmlInput: {
              ...params.slotProps.htmlInput,
              ...textFieldProps?.slotProps?.htmlInput,
              autoComplete: 'new-password',
            },
          }}
        />
      )}
      renderOption={(optionProps, option) => {
        if (option.userId === EMPTY_PLACEHOLDER_ID) {
          return (
            <ContactSuggestionsNoResults
              interactionId={interactionId}
              interactionOwnerId={userId}
              googleAccountLinked={isGoogleAccountLinked}
              followUpBossAccountLinked={isFollowUpBossAccountLinked}
              thirdPartyContactsIncluded={
                includeGoogleContacts || includeFollowUpBossContacts
              }
              compact={interactionType === InteractionType.referral}
            />
          );
        }

        if (!option.googleId && !option.followUpBossId && !option.userId)
          return null;

        return (
          <li
            {...optionProps}
            key={
              // A user can appear once per team membership, so the user id
              // alone is not unique across suggestions.
              (option.userId &&
                `${option.userId}:${option.contactResponse?.teamId}`) ||
              option.googleId ||
              option.followUpBossId ||
              option.displayName
            }
          >
            <Grid
              container
              sx={{ alignItems: 'flex-start', flexWrap: 'nowrap' }}
            >
              <Grid>
                <Box sx={{ mr: 2 }}>
                  <UserAvatar
                    avatarUrl={option.photoUrl ?? undefined}
                    name={`${option.firstName} ${option.lastName}`}
                  />
                </Box>
              </Grid>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  wordBreak: 'break-word',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: 'text.primary' }}
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: We do this for highlighting
                  dangerouslySetInnerHTML={{
                    __html: highlightSubstring(option.displayName, searchValue),
                  }}
                />
                {option.email && (
                  <Typography
                    variant="body2"
                    sx={{
                      pt: 0.25,
                      color: 'text.secondary',
                    }}
                  >
                    {option.email}
                  </Typography>
                )}
              </Box>
            </Grid>
          </li>
        );
      }}
      slots={{
        paper: PaperComponent,
      }}
      slotProps={{
        paper: {
          ...paperProps,
          sx: {
            ...paperProps?.sx,
            ...(!searchValue.trim() ? { display: 'none' } : {}),
          },
        },
      }}
    />
  );
}
